import type {PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import {getRelayProvisioning, getRelayStatus, subscribeRelayRegistry} from '@github-ui/ahp-relay/relay-registry'

export type TaskStartupPhase = 'connecting' | 'preparing' | 'cloning' | 'starting'

export type TaskStartup =
  | {
      readonly status: 'pending'
      readonly phase: TaskStartupPhase
      readonly prompt: PreparedPrompt
    }
  | {
      readonly status: 'failed'
      readonly phase: TaskStartupPhase
      readonly prompt: PreparedPrompt
      readonly error: string
    }

interface TaskStartupRecord {
  snapshot: TaskStartup
  failureObserved: boolean
  consumers: number
  cleanupTimer?: ReturnType<typeof setTimeout>
}

const FAILURE_CLEANUP_GRACE_MS = 5_000
const PHASE_ORDER: Record<TaskStartupPhase, number> = {
  connecting: 0,
  preparing: 1,
  cloning: 2,
  starting: 3,
}

const records = new Map<string, TaskStartupRecord>()
const listeners = new Set<() => void>()
let unsubscribeRelayRegistry: (() => void) | undefined

function notify(): void {
  for (const listener of listeners) listener()
}

function hasPendingStartup(): boolean {
  return [...records.values()].some(record => record.snapshot.status === 'pending')
}

function updateRelaySubscription(): void {
  if (hasPendingStartup()) {
    unsubscribeRelayRegistry ??= subscribeRelayRegistry(refreshPendingStartups)
  } else {
    unsubscribeRelayRegistry?.()
    unsubscribeRelayRegistry = undefined
  }
}

function scheduleFailureCleanup(taskId: string, record: TaskStartupRecord): void {
  if (record.snapshot.status !== 'failed' || !record.failureObserved || record.consumers > 0 || record.cleanupTimer)
    return

  record.cleanupTimer = setTimeout(() => {
    const current = records.get(taskId)
    if (current !== record || current.snapshot.status !== 'failed' || current.consumers > 0) return
    records.delete(taskId)
    notify()
  }, FAILURE_CLEANUP_GRACE_MS)
}

function advanceTaskStartup(taskId: string): void {
  const record = records.get(taskId)
  if (!record || record.snapshot.status !== 'pending') return

  const provisioning = getRelayProvisioning(taskId)
  if (provisioning?.phase === 'failed') {
    failTaskStartup(taskId, provisioning.error ?? 'Repository preparation failed.')
    return
  }

  let phase = record.snapshot.phase
  if (provisioning?.phase === 'preparing') phase = 'preparing'
  if (provisioning?.phase === 'cloning') phase = 'cloning'
  if (provisioning?.phase === 'ready' || getRelayStatus(taskId) === 'connected') phase = 'starting'
  if (PHASE_ORDER[phase] <= PHASE_ORDER[record.snapshot.phase]) return

  record.snapshot = {...record.snapshot, phase}
  notify()
}

function refreshPendingStartups(): void {
  for (const taskId of records.keys()) advanceTaskStartup(taskId)
}

export function subscribeTaskStartupStore(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getTaskStartup(taskId: string | undefined): TaskStartup | undefined {
  return taskId ? records.get(taskId)?.snapshot : undefined
}

export function getTaskStartupPhaseMessage(taskStartup: TaskStartup): string {
  switch (taskStartup.phase) {
    case 'connecting':
      return 'Connecting to environment…'
    case 'preparing':
      return 'Preparing repository…'
    case 'cloning':
      return 'Cloning repository…'
    case 'starting':
      return 'Starting agent…'
  }
}

export function getTaskStartupPresentation(
  taskStartup: TaskStartup | undefined,
  cloneProgress: number | undefined,
): {message: string; progress?: number} | undefined {
  if (taskStartup?.status !== 'pending') return undefined

  return {
    message: getTaskStartupPhaseMessage(taskStartup),
    ...(taskStartup.phase === 'cloning' && cloneProgress !== undefined ? {progress: cloneProgress} : {}),
  }
}

/** Starts the task-scoped lifecycle before detached relay work can race navigation. */
export function beginTaskStartup(taskId: string, prompt: PreparedPrompt): void {
  const existing = records.get(taskId)
  if (existing?.cleanupTimer) clearTimeout(existing.cleanupTimer)
  records.set(taskId, {
    snapshot: {status: 'pending', phase: 'connecting', prompt},
    failureObserved: false,
    consumers: 0,
  })
  updateRelaySubscription()
  advanceTaskStartup(taskId)
  notify()
}

/** Completes only after first-turn acceptance, handing rendering to canonical AHP state. */
export function completeTaskStartup(taskId: string): void {
  const record = records.get(taskId)
  if (!record) return
  if (record.cleanupTimer) clearTimeout(record.cleanupTimer)
  records.delete(taskId)
  updateRelaySubscription()
  notify()
}

/** Retains failures until the task surface has observed and finally released them. */
export function failTaskStartup(taskId: string, error: unknown): void {
  const record = records.get(taskId)
  if (!record || record.snapshot.status !== 'pending') return
  record.snapshot = {
    ...record.snapshot,
    status: 'failed',
    error: error instanceof Error ? error.message : String(error),
  }
  updateRelaySubscription()
  scheduleFailureCleanup(taskId, record)
  notify()
}

/** Marks a startup visible and keeps an observed failure alive across brief remounts. */
export function retainTaskStartup(taskId: string): () => void {
  const record = records.get(taskId)
  if (!record) return () => {}
  if (record.cleanupTimer) {
    clearTimeout(record.cleanupTimer)
    record.cleanupTimer = undefined
  }
  if (record.snapshot.status === 'failed') record.failureObserved = true
  record.consumers += 1
  let released = false

  return () => {
    if (released || records.get(taskId) !== record) return
    released = true
    record.consumers = Math.max(0, record.consumers - 1)
    scheduleFailureCleanup(taskId, record)
  }
}

/** Test-only: clears all task startup state and relay observation. */
export function __resetTaskStartupStore(): void {
  for (const record of records.values()) {
    if (record.cleanupTimer) clearTimeout(record.cleanupTimer)
  }
  records.clear()
  unsubscribeRelayRegistry?.()
  unsubscribeRelayRegistry = undefined
  notify()
}
